import type { DTO } from '@/type-utils';
import type { CardNetwork } from '@/constructs/CardNetwork';
import { computed } from 'mobx';
import Model from '../Model';
import type { ISavedPayment } from './ISavedPayment';
import { AddressModel } from '../Address';

/**
 * A model representing a saved payment method.
 */
export class SavedPaymentModel extends Model<DTO<ISavedPayment>> {
  private _nickname?: string;
  private _uuid: string;
  private _cardType: CardNetwork;
  private _maskedNumber: string;
  private _expiration: string;
  private _billingAddress: AddressModel;
  private _isPreferred?: boolean;

  /** @inheritdoc */
  public get nickname(): string | undefined {
    return this._nickname;
  }

  /** @inheritdoc */
  public get uuid(): string {
    return this._uuid;
  }

  /** @inheritdoc */
  public get cardType(): CardNetwork {
    return this._cardType;
  }

  /** @inheritdoc */
  public get maskedNumber(): string {
    return this._maskedNumber;
  }

  /** @inheritdoc */
  public get expiration(): string {
    return this._expiration;
  }

  /** @inheritdoc */
  public get billingAddress(): AddressModel {
    return this._billingAddress;
  }

  /** @inheritdoc */
  public get isPreferred(): boolean | undefined {
    return this._isPreferred;
  }

  /**
   * Get the last four digits of the saved payment.
   * @returns - The last four digits.
   */
  @computed public get endingDigits(): string {
    return this._maskedNumber.slice(-4);
  }

  /** @inheritdoc */
  public constructor(data: DTO<ISavedPayment>) {
    super(data);

    this._nickname = data.nickname;
    this._uuid = data.uuid;
    this._cardType = data.cardType;
    this._maskedNumber = data.maskedNumber;
    this._expiration = data.expiration;
    this._isPreferred = data.isPreferred;
    this._billingAddress = new AddressModel(data.billingAddress);
  }

  /** @inheritdoc */
  public override toDTO(): DTO<ISavedPayment> {
    return {
      nickname: this._nickname,
      uuid: this._uuid,
      cardType: this._cardType,
      maskedNumber: this._maskedNumber,
      expiration: this._expiration,
      isPreferred: this._isPreferred,
      billingAddress: this._billingAddress.toDTO()
    };
  }
}
